import jlrLogoUrl from "../../../assets/imgs/home/JLR.svg";

const BRANDS = ["Land Rover", "Jaguar"];

/** Constech enrollment form per brand. The URL fixes the OEM, so brand is not sent. */
const ENROLLMENT_FORM_URLS = {
	"Land Rover": "https://app.constech.io/land-rover-enrollment-form",
	Jaguar: "https://app.constech.io/jaguar-enrollment-form",
};

/** Case-sensitive, in spec order. Never send a param named `edit`: it disables pre-fill. */
const ENROLLMENT_PARAMS = ["firstName", "lastName", "title", "email", "phone", "website"];

/** The enrollment form ignores anything past this length. */
const ENROLLMENT_PARAM_MAX_LENGTH = 200;

/** Matches the spec's example: blank values left out, phone as digits only, spaces as %20. */
function enrollmentUrl(brand, values = {}) {
	const params = new URLSearchParams();
	ENROLLMENT_PARAMS.forEach((key) => {
		let value = (values[key] ?? "").trim();
		if (key === "phone") value = value.replace(/\D/g, "");
		value = value.slice(0, ENROLLMENT_PARAM_MAX_LENGTH);
		if (value) params.set(key, value);
	});
	// URLSearchParams writes spaces as "+"; a literal "+" is already encoded as %2B.
	const query = params.toString().replace(/\+/g, "%20");
	return `${ENROLLMENT_FORM_URLS[brand]}${query ? `?${query}` : ""}`;
}

const jlr = {
	id: "jlr",
	pageTitle: "JLR Retailer Website Offer Automation | Enrollment",

	navLinks: [
		{ label: "How it works", href: "#how" },
		{ label: "What's included", href: "#included" },
		{ label: "Packages", href: "#packages" },
		{ label: "Enroll", href: "#enroll" },
	],

	header: {
		label: "JLR Retailer Digital Program",
		logos: [{ src: jlrLogoUrl, alt: "JLR", width: 44, height: 40 }],
	},

	hero: {
		hat: "JLR Retailer Digital Program",
		titleLines: ["Website offers,", "automated"],
		lead: "Every JLR national and regional lease, finance and cash program, matched to your in-stock VINs and published to your Land Rover and Jaguar websites with your own disclosures and fees. No rekeying, no vendor tickets, refreshed every month.",
		primaryCta: "Enroll your retailer",
		secondaryCta: "View packages",
		image: require("../../../assets/imgs/automated-offers/lr-defender.jpeg"),
		imageMobile: require("../../../assets/imgs/automated-offers/lr-defender-mobile.jpeg"),
		imageAlt:
			"Example automated offer tile: 2026 Defender P300 S 110, lease for $879 a month for 36 months, $6,954 due at signing",
		caption:
			"Example VIN-level offer, built from the JLR program and the retailer's approved disclosures.",
	},

	statement: [
		"Offers are rule-based, driven by JLR and counsel-approved rules, and built at the VIN level.",
	],

	how: {
		title: "How it works",
		sub: "JLR sets the programs. Constellation applies them to your inventory and keeps your website current, VIN by VIN.",
		steps: [
			{
				step: "01",
				title: "JLR defines the programs",
				body: "National and regional lease, finance and cash programs, with JLR program rules enforced on every offer.",
			},
			{
				step: "02",
				title: "Each VIN gets its offer",
				body: "Eligible VINs are matched to the right program. Amount due at signing adjusts to the VIN and MSRP while your approved fees and disclosures stay in place.",
			},
			{
				step: "03",
				title: "Your site updates",
				body: "Offer pages and VIN pages publish in minutes. When a vehicle sells or a program changes, the offer updates or expires.",
			},
		],
	},

	included: {
		title: "What's included",
		sub: "Everything below is covered by the monthly fee per retailer website.",
		collage: {
			image: require("../../../assets/imgs/heavy-up/collage.jpg"),
			imageAlt:
				"Automated offer creative for Land Rover retailer websites across Defender, Discovery and Range Rover models",
		},
		groups: [
			{
				title: "Offers and integrations",
				items: [
					{
						title: "Monthly offer refresh",
						timing: "24 to 48 hours",
						body: "New offers live within 24 to 48 hours after the previous month's offers expire.",
					},
					{
						title: "Sold-vehicle creative refresh",
						body: "Creative refreshes automatically when a VIN sells, at no added feature fee.",
					},
					{
						title: "Dealer.com (DDC) integration",
						body: "Offers publish to DDC offer and VIN pages.",
					},
					{
						title: "Dealer Inspire integration",
						body: "Expected to take longer than two weeks; timing confirmed during onboarding.",
					},
				],
			},
			{
				title: "Compliance and configuration",
				items: [
					{
						title: "Your disclosures",
						timing: "Configured once",
						body: "Retailer-specific legal language for lease, finance and cash, applied to each offer.",
					},
					{
						title: "Document fee and accessories",
						timing: "Per retailer",
						body: "Your document fee is configured for your store, and retailer-installed accessories are accounted for in the advertised price.",
					},
					{
						title: "Region and jurisdiction rules",
						timing: "Automatic",
						body: "Regional programs and market requirements applied where they apply.",
					},
					{
						title: "FTC and California CARS Act",
						timing: "Built in",
						body: "Offers are built to FTC price-transparency expectations and CARS Act (SB 766) requirements once you supply complete pricing, fee, accessory and disclosure inputs.\n\nBuilt in after the Retailer discloses their additional fees.",
					},
				],
			},
		],
	},

	packagesSection: {
		title: "Packages",
		sub: "Pricing is per retailer website, per month. Land Rover and Jaguar websites are enrolled separately.",
		items: [
			{
				pick: "single",
				who: "One brand website, Land Rover or Jaguar",
				amount: "$250",
				per: "per website, per month",
				features: [
					"All JLR national and regional programs",
					"Offer pages and VIN pages",
					"No minimum commitment",
				],
				cta: "Choose one brand",
			},
			{
				pick: "both",
				who: "Land Rover and Jaguar websites together",
				amount: "$500",
				per: "per month for both websites",
				features: [
					"Both brand websites, each at $250",
					"One onboarding for disclosures and fees",
					"No minimum commitment",
				],
				cta: "Choose both brands",
			},
			{
				pick: "media",
				who: "Existing Constellation media clients",
				amount: "$150",
				per: "per website, per month",
				features: [
					"Same functionality as standard",
					"Applies regardless of volume",
					"$300 per month for both brands",
				],
				cta: "Choose media client rate",
			},
			{
				pick: "mediaBoth",
				who: "Existing Constellation media clients, Land Rover and Jaguar websites together",
				amount: "$300",
				per: "per month for both websites",
				features: [
					"Both brand websites, each at $150",
					"One onboarding for disclosures and fees",
					"No minimum commitment",
				],
				cta: "Choose both brands, media client",
			},
		],
		volume: {
			title: "Retailer group volume pricing",
			body: "Groups enrolling at scale, including groups that consolidate advertising with Constellation, qualify for a lower per-website rate based on their commitment.",
			note: "Group rates are confirmed with Constellation at contract. Please reach out if you want to enroll the entire group.",
			cta: "Book a demo",
			ctaHref: "https://www.helloconstellation.com/book-a-demo",
		},
	},

	timelineSection: {
		title: "Launch timeline",
		sub: "Most DDC retailers can be live within two weeks of enrolling.",
		items: [
			{
				when: "Immediately",
				title: "Constellation ready",
				body: "Onboarding begins and your disclosures, document fee and accessories are configured.",
			},
			{
				when: "Within 2 weeks",
				title: "DDC integration",
				body: "Offers connect to your DDC offer and VIN pages. Dealer Inspire timing is confirmed separately.",
			},
			{
				when: "Following integration",
				title: "Your website goes live",
				body: "Offers refresh every month and update as inventory changes.",
			},
		],
	},

	enroll: {
		title: "Enroll your retailer",
		sub: "Tell us about your store and websites. Constellation will confirm your package and schedule onboarding.",
	},

	footer: {
		logos: [{ src: jlrLogoUrl, alt: "JLR", width: 44, height: 40 }],
		notes: [
			"Pricing and timelines reflect the JLR Website Automated Offers program, September 2026. Integration timelines and any third-party fees remain subject to the website provider's requirements. Automatic sold-vehicle creative refresh is targeted for December 2026.",
			"Constellation, One World Trade Center, 285 Fulton St, Floor 21, New York, NY 10007",
		],
	},

	form: {
		variant: "fillEnrollment",
		brands: BRANDS,
		packageOptions: [
			{ id: "standard", label: "Standard", rate: 250 },
			{ id: "media", label: "Media client", rate: 150 },
			/** No fixed rate: confirmed with Constellation at contract. */
			{ id: "group", label: "Retailer group", rate: null },
		],
		packagePicks: {
			single: { package: "standard", brands: ["Land Rover"] },
			both: { package: "standard", brands: ["Land Rover", "Jaguar"] },
			media: { package: "media", brands: ["Land Rover"] },
			mediaBoth: { package: "media", brands: ["Land Rover", "Jaguar"] },
		},
		providerOptions: ["Dealer.com (DDC)", "Dealer Inspire", "Other"],
		providerHint(provider) {
			if (provider === "Dealer Inspire")
				return "Dealer Inspire integration takes longer than 2 weeks; timing is confirmed during onboarding.";
			if (provider === "Other")
				return "Constellation will confirm integration timing with your provider.";
			return "DDC integration takes about 2 weeks.";
		},
		labels: {
			code: "JLR retailer code",
			websiteNoun: "retailer website",
			urlPlaceholders: {
				"Land Rover": "https://www.landrover…com",
				Jaguar: "https://www.jaguar…com",
			},
		},
		/** The form appends "available here." with the terms link. */
		authorizationText:
			"By checking this box and submitting this Enrollment Form, I affirm that I am authorized to act on behalf of the dealership(s) identified herein (“Dealership”) and to enroll in the selected services. Dealership understands that offer compliance depends on Dealership supplying complete and accurate pricing, fee, accessory and disclosure inputs. Dealership authorizes Constellation Software, Inc. (“Constellation”) to direct bill the OEM on a monthly basis via the parts statement for all selected services. Dealership agrees that the selected services are subject to Constellation’s standard terms and conditions and E-SIGN Act Policy,",
		termsUrl: "https://www.helloconstellation.com/terms-and-conditions",
		/** JotForm — JLR Automated Offers. */
		jotformId: "262874486205061",
		/** JotForm field names; one submission per website. */
		jotformFields: {
			firstName: "q3_firstName",
			lastName: "q4_typeA4",
			title: "q5_typeA5",
			retailerCode: "q6_typeA6",
			email: "q7_email",
			phone: "q9_phone",
			package: "q10_package",
			ratePerSite: "q11_typeA11",
			brand: "q12_typeA12",
			websiteUrl: "q13_typeA13",
			provider: "q14_typeA14",
			authorized: "q15_typeA15",
			websitesSelected: "q16_typeA16",
			monthlyEstimate: "q17_typeA17",
		},
		enrollmentUrl,
	},
};

export default jlr;
